import React from 'react';
import { Box, Typography, Button, Divider } from '@mui/material';
import { StyleSheet } from 'react-native';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EsResetWrapper } from '../EsResetPassword/EsResetWrapper';
import { useEsHover } from '../Animation/useEsHover';
import { AppStoreIcon } from '../CustomSVGIcons/AppStoreIcon';
import { GooglePlayStoreIcon } from '../CustomSVGIcons/GooglePlayStoreIcon';
import { QRCode } from 'react-qrcode-logo';
import { thspian_light_logo, thspian_logo } from '../../asset/images';

const APP_STORE_URL = 'https://apps.apple.com/app/audme/id1523966910'; // Replace with actual App Store URL
const GOOGLE_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.thspian.audme&pli=1'; // Replace with actual Google Play Store URL

interface AudmeQRCodeStepProps {
    qrCode: string;
    deeplink: string;
    onReady: () => void;
    onBack: () => void;
}

export const AudmeQRCodeStep: React.FC<AudmeQRCodeStepProps> = ({ qrCode, deeplink, onReady, onBack }) => {
    const { themeStyle } = useThemeContext();
    const { handleMouseEnter, handleMouseLeave, isHover } = useEsHover();
    const {
        handleMouseEnter: handleOpenAppHover,
        handleMouseLeave: handleOpenAppLeave,
        isHover: isOpenAppHover,
    } = useEsHover();
    const styles = dynamicStyles(themeStyle, isHover, isOpenAppHover);

    const handleOpenApp = () => {
        window.location.href = deeplink;
    };

    const handleAppStoreClick = () => {
        window.open(APP_STORE_URL, '_blank');
    };

    const handleGooglePlayStoreClick = () => {
        window.open(GOOGLE_PLAY_STORE_URL, '_blank');
    };

    return (
        <EsResetWrapper title="Setup Audme App" subtitle="Tap either button to download the app">
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
                {/* Store Icons */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        mb: 3,
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: 400,
                    }}>
                    <Box
                        onClick={handleAppStoreClick}
                        sx={{
                            width: { xs: 130, sm: 150 },
                            height: { xs: 36, sm: 42 },
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s',
                            overflow: 'hidden',
                            '&:hover': {
                                opacity: 0.8,
                            },
                        }}>
                        <AppStoreIcon
                            style={{
                                padding: '4px',
                            }}
                        />
                    </Box>
                    <Box
                        onClick={handleGooglePlayStoreClick}
                        sx={{
                            width: { xs: 130, sm: 150 },
                            height: { xs: 36, sm: 42 },
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s',
                            '&:hover': {
                                opacity: 0.8,
                            },
                        }}>
                        <GooglePlayStoreIcon
                            style={{
                                padding: '4px',
                            }}
                        />
                    </Box>
                </Box>

                {/* Divider */}
                <Divider sx={{ width: '100%', mb: 2, borderColor: theme[themeStyle].tag_white_light }} />

                {/* Scan QR Code Text */}
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: theme[themeStyle].light_gray_text,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        mb: 2,
                    }}>
                    Scan QR Code with Audme or device camera
                </Typography>

                {/* QR Code Display */}
                <Box
                    sx={{
                        width: 220,
                        height: 220,
                        backgroundColor: theme[themeStyle].form_black,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        mb: 2,
                        p: 2,
                    }}>
                    {qrCode && deeplink ? (
                        <QRCode
                            value={deeplink}
                            ecLevel="M"
                            fgColor={theme[themeStyle].highlight}
                            bgColor="transparent"
                            eyeRadius={30}
                            logoImage={themeStyle === 'dark' ? thspian_logo : thspian_light_logo}
                            logoWidth={40}
                            logoHeight={40}
                            quietZone={10}
                            removeQrCodeBehindLogo
                            logoPaddingStyle="circle"
                            logoPadding={20}
                            qrStyle="dots"
                            size={200}
                        />
                    ) : (
                        <Typography
                            sx={{
                                color: theme[themeStyle].light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Loading QR Code...
                        </Typography>
                    )}
                </Box>

                {/* Instructional Text */}
                <Typography
                    sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: theme[themeStyle].light_gray_text,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        mb: 3,
                        textAlign: 'center',
                    }}>
                    Or, tap below to open the Audme app if already installed
                </Typography>

                {/* Open App Button */}
                <Button
                    onMouseEnter={handleOpenAppHover}
                    onMouseLeave={handleOpenAppLeave}
                    style={styles.openAppButton}
                    onClick={handleOpenApp}
                    type="button">
                    <Typography style={styles.openAppText}>Open App</Typography>
                </Button>

                {/* I'm Ready Button */}
                <Button
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    style={styles.readyButton}
                    onClick={onReady}
                    type="button">
                    <Typography style={styles.readyText}>Enter 2FA Code</Typography>
                </Button>

                {/* Back Button */}
                <Button style={styles.backButton} onClick={onBack} type="button">
                    <Typography style={styles.backText}>Go Back</Typography>
                </Button>
            </Box>
        </EsResetWrapper>
    );
};

const dynamicStyles = (colorScheme: ColorScheme, isHover: boolean, isOpenAppHover: boolean) =>
    StyleSheet.create({
        openAppButton: {
            width: '100%',
            height: 44,
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderRadius: 100,
            maxWidth: 400,
            backgroundColor: theme[colorScheme].true_black,
            textTransform: 'none',
            alignSelf: 'center',
            marginTop: 10,
            marginBottom: 10,
            color: theme[colorScheme].true_white,
        },
        openAppText: {
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: theme[colorScheme].true_white,
        },
        readyButton: {
            width: '100%',
            height: 44,
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderRadius: 100,
            maxWidth: 400,
            backgroundColor: isHover ? theme[colorScheme].gray_text : theme[colorScheme].primary_orange,
            textTransform: 'none',
            alignSelf: 'center',
            marginTop: 10,
        },
        readyText: {
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: isHover ? theme[colorScheme].primary_orange : theme[colorScheme].gray_text,
        },
        backText: {
            textAlign: 'center',
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: '400',
            color: theme[colorScheme].lightWhite,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        backButton: {
            width: 'fit-content',
            alignSelf: 'center',
            height: 'fit-content',
            paddingHorizontal: 20,
            paddingVertical: 5,
            backgroundColor: theme[colorScheme].transparent,
            textTransform: 'none',
            marginTop: 20,
        },
    });
